import { yupResolver } from '@hookform/resolvers/yup'
import { Scan } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { PageLayout } from '@/components'
import { Card, CardContent } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormGroup } from '@/components/ui/form-group'
import { forgotPassword } from '../../services/auth'
import { FeatureCarousel } from '../FeatureCarousel'

interface NewPasswordValues {
  password: string
  passwordConf: string
}

interface LocationState {
  phone: string
  validationCode: string
}

const NewPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Get data from location state or sessionStorage
  const phoneFromState = (location.state as LocationState)?.phone
  const validationCodeFromState = (location.state as LocationState)
    ?.validationCode
  const phoneFromStorage = sessionStorage.getItem('new_password_phone')
  const validationCodeFromStorage = sessionStorage.getItem(
    'new_password_validation_code',
  )

  const phone = phoneFromState || phoneFromStorage || ''
  const validationCode =
    validationCodeFromState || validationCodeFromStorage || ''

  useEffect(() => {
    if (!validationCode || !phone) {
      navigate('/account/login')
    }
  }, [validationCode, phone, navigate])

  const submitNewPassword = async (values: NewPasswordValues) => {
    const response = await forgotPassword(
      phone,
      values.password,
      validationCode,
    )

    if (response && response.status === 200) {
      // Clear sessionStorage on success
      sessionStorage.removeItem('new_password_phone')
      sessionStorage.removeItem('new_password_validation_code')
      sessionStorage.removeItem('reset_password_phone')

      navigate('/account/login')
    }
  }

  const schema = yup.object().shape({
    password: yup.string().required('Password is required'),
    passwordConf: yup
      .string()
      .required('Password is required')
      .oneOf([yup.ref('password')], 'Passwords must match'),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<NewPasswordValues>({
    mode: 'onChange',
    resolver: yupResolver<NewPasswordValues>(schema),
  })

  const features = [
    {
      icon: (
        <div className="w-16 h-16 rounded-2xl bg-accent-1-60 flex items-center justify-center">
          <Scan className="w-8 h-8 text-accent-1" />
        </div>
      ),
      title: 'Fast & Secure Check-In',
      description:
        'Say goodbye to paper tickets and manual lists. Our blockchain-powered check-in system allows for instant ticket validation—whether scanned at the door or verified on-chain.',
    },
  ]

  return (
    <PageLayout authPage>
      <div className="container flex mt-12">
        {/* Left Side - Features Section */}
        <div className="relative w-1/2 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <FeatureCarousel features={features} />
          </div>
        </div>

        {/* Right Side - Form */}
        <Card className="w-[527px]">
          <CardContent className="flex flex-col gap-5 items-center w-full px-24 py-16">
            <div className="w-full">
              <div className="text-title-m text-center mb-2">New Password</div>
              <div className="text-body-medium text-monochrome-2 text-center mb-8">
                Please enter your new password.
              </div>

              <form
                onSubmit={handleSubmit(submitNewPassword)}
                className="space-y-7"
              >
                <FormGroup
                  label="New Password"
                  error={errors.password?.message}
                >
                  <Input
                    id="password"
                    type="password"
                    {...register('password')}
                    placeholder="New Password"
                  />
                </FormGroup>

                <FormGroup
                  label="Repeat Password"
                  error={errors.passwordConf?.message}
                >
                  <Input
                    id="passwordConf"
                    type="password"
                    {...register('passwordConf')}
                    placeholder="Repeat Password"
                  />
                </FormGroup>

                <Button className="w-full" type="submit">
                  Create Password & Login
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}

export default NewPasswordPage
